import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { cancelCreditDebitNote } from "../../services";
import { localizeApiMessage } from "../../errors/messages";
import {
  CreditDebitNoteDetail,
  CreditDebitNoteStockShortfall,
} from "../../types/credit-debit-note.type";
import { formatNumber } from "../../utils";
import { apiErrorMessage } from "../SpecialOrder/data";
import { NOTE_TEXT_MAX_LENGTH, NOTE_TYPE_LABEL } from "./constants";

interface CancelNoteDialogProps {
  open: boolean;
  note: CreditDebitNoteDetail;
  onClose: () => void;
  onCancelled: () => void;
}

const FALLBACK_ERROR = "ยกเลิกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/** Where a 409 carries the van items the goods cannot be taken back out of. */
const SHORTFALLS_PATH = ["response", "data", "error", "shortfalls"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** `value[key]` when value is an object — for reading an error of unknown shape. */
const fieldOf = (value: unknown, key: string): unknown =>
  isRecord(value) ? value[key] : undefined;

const isShortfall = (value: unknown): value is CreditDebitNoteStockShortfall =>
  isRecord(value) &&
  typeof value.product_code === "string" &&
  typeof value.current_qty === "number" &&
  typeof value.return_qty === "number" &&
  typeof value.after_qty === "number";

/**
 * The van items a 409 says cannot give the returned goods back (`error.shortfalls`): sold on
 * since the note was issued. Any other refusal answers none.
 */
const stockShortfallsOf = (error: unknown): CreditDebitNoteStockShortfall[] => {
  const shortfalls = SHORTFALLS_PATH.reduce<unknown>(fieldOf, error);
  return Array.isArray(shortfalls) ? shortfalls.filter(isShortfall) : [];
};

const formatQty = (value: number) => formatNumber(value, { decimals: 0 });

const shortfallText = (shortfall: CreditDebitNoteStockShortfall) =>
  `${shortfall.product_code} ${shortfall.product_name ?? ""} — บนรถเหลือ ${formatQty(
    shortfall.current_qty,
  )} ต้องดึงคืน ${formatQty(shortfall.return_qty)} ขาด ${formatQty(-shortfall.after_qty)}`;

/**
 * Cancel an issued note. An issued note is never edited, so a mistake is cancelled with a
 * reason and a new note issued. A note that put goods back into van stock takes them out again,
 * so a cancel is refused when the van no longer holds them; the refusal (with what is short) is
 * shown inside the dialog, not as a toast behind it.
 */
export default function CancelNoteDialog({
  open,
  note,
  onClose,
  onCancelled,
}: Readonly<CancelNoteDialogProps>) {
  const [reason, setReason] = useState<string>("");
  const [reasonError, setReasonError] = useState<string>("");
  const [requestError, setRequestError] = useState<string>("");
  const [shortfalls, setShortfalls] = useState<CreditDebitNoteStockShortfall[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const takesStockBack = Boolean(note.stock_returned_at && !note.stock_reverted_at);

  useEffect(() => {
    if (open) {
      setReason("");
      setReasonError("");
      setRequestError("");
      setShortfalls([]);
    }
  }, [open]);

  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
    setReasonError("");
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setReasonError("กรุณาระบุเหตุผลการยกเลิก");
      return;
    }
    setLoading(true);
    setRequestError("");
    setShortfalls([]);
    try {
      await cancelCreditDebitNote(note.uuid, { reason: trimmedReason });
      onCancelled();
    } catch (error) {
      const message = apiErrorMessage(error);
      setRequestError(message ? localizeApiMessage(message) : FALLBACK_ERROR);
      setShortfalls(stockShortfallsOf(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>ยกเลิก{NOTE_TYPE_LABEL[note.note_type]}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>
          ยกเลิก{NOTE_TYPE_LABEL[note.note_type]} {note.note_no} แล้วแก้ไขไม่ได้
          ถ้าต้องการแก้ไขให้ออกเอกสารใหม่
        </Typography>
        {takesStockBack && (
          <Typography variant="body2" mb={2}>
            ระบบจะดึงสินค้าที่รับคืนออกจากสต็อกบนรถสาย {note.return_route_code}
          </Typography>
        )}
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="เหตุผลการยกเลิก"
          required
          value={reason}
          onChange={handleReasonChange}
          error={Boolean(reasonError)}
          helperText={reasonError}
          slotProps={{ htmlInput: { maxLength: NOTE_TEXT_MAX_LENGTH } }}
        />
        {requestError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {requestError}
            {shortfalls.map((shortfall) => (
              <Typography key={shortfall.product_code} variant="body2" mt={0.5}>
                {shortfallText(shortfall)}
              </Typography>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          ปิด
        </Button>
        <Button variant="contained" color="error" loading={loading} onClick={handleConfirm}>
          ยืนยันยกเลิก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
